import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import { useCalculateAdditionalToolScoreWithPostRoute } from '@/hooks';
import type { AdditionalItem, GetAdditionalToolScoreDTO } from '@/hooks';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useStepKeysContext } from '@/components/views/EwaJdsD86/Report/components/StepKeys/DefaultSelects/context';
import { worstRiskCalculator } from '@/components/views/EwaJdsD86/Report/components/StepKeys/AdditionalComponents/utils/WorstResultCalculator';

const { useFormInstance, useWatch } = Form;

export interface PersistResultEntryParams {
	value_1?: number;
	value_2?: number;
	clearFieldName: string[];
}

interface UseInputAndInputBaseProps {
	additionalItem: AdditionalItem;
}

interface UseInputAndInputBaseReturn {
	id: string;
	formFieldName: string[];
	formValueOne: number;
	formValueTwo: number;
	localAdditionalItem: AdditionalItem;
	worstRisk: number;
	isCalculatingScore: boolean;
	persistResultEntry: (params: PersistResultEntryParams) => Promise<void>;
	handleRemoveAdditionalItemClick: (removedId: string) => void;
}

export function useInputAndInputBase({
	additionalItem
}: Readonly<UseInputAndInputBaseProps>): UseInputAndInputBaseReturn {
	const { id } = additionalItem;

	const form = useFormInstance();
	const { formFieldName, stepKey } = useStepKeysContext();
	const { organization, company } = useApplicationContext();

	const [localAdditionalItem, setLocalAdditionalItem] = useState<AdditionalItem>(additionalItem);

	const formValueOne = useWatch([...formFieldName, 'additional_items', id, 'value_1']);
	const formValueTwo = useWatch([...formFieldName, 'additional_items', id, 'value_2']);

	useEffect(() => {
		form.setFieldValue([...formFieldName, 'additional_items', 'local_values'], additionalItem);
	}, []);

	const { mutateAsync: calculateAdditionalToolScore, isLoading: isCalculatingScore } =
		useCalculateAdditionalToolScoreWithPostRoute();

	const worstRisk = worstRiskCalculator.getWorstResult(localAdditionalItem.results);

	async function persistResultEntry({ value_1, value_2, clearFieldName }: PersistResultEntryParams): Promise<void> {
		const payload: GetAdditionalToolScoreDTO = {
			value_1,
			value_2,
			company_id: company?.id,
			organization_id: organization?.id,
			custom_report_step_key_id: stepKey.id
		};

		const additionalToolScore = await calculateAdditionalToolScore(payload);

		if (!additionalToolScore?.result) {
			return;
		}

		const newAdditionalItemValue = {
			id: uuidv4(),
			value_1,
			value_2,
			value_3: additionalToolScore.value_3,
			result: additionalToolScore.result
		};

		const previousResults = localAdditionalItem?.results ?? [];
		const nextResults = [...previousResults, newAdditionalItemValue];

		setLocalAdditionalItem({
			...localAdditionalItem,
			results: nextResults
		});

		form.setFieldValue([...formFieldName, 'additional_items', 'local_values', 'results'], nextResults);
		form.setFieldValue(clearFieldName, undefined);
	}

	function handleRemoveAdditionalItemClick(removedId: string): void {
		const nextResults = localAdditionalItem.results.filter((item) => item.id !== removedId);

		setLocalAdditionalItem({
			...localAdditionalItem,
			results: nextResults
		});

		form.setFieldValue([...formFieldName, 'additional_items', 'local_values', 'results'], nextResults);
	}

	return {
		id,
		formFieldName,
		formValueOne,
		formValueTwo,
		localAdditionalItem,
		worstRisk,
		isCalculatingScore,
		persistResultEntry,
		handleRemoveAdditionalItemClick
	};
}
